/**
 * Per-model criteria for the V2 Model Score filter. One entry per selected
 * scoring model. The selection mirrors the active scoring sources (toolbar
 * pills): the page seeds entries from the active models when the filters modal
 * opens, and confirming makes the active set match the selection. An entry
 * with no criteria is selection only — it activates the model without
 * filtering, and is never serialized (see mapper.ts).
 */
import {
  MODEL_SCORE_PILLARS,
  type ModelScorePillar,
} from "@/lib/scoring/modelScoreFields";

export type ScoreRange = { min?: number; max?: number };

export type ModelScoreEntry = {
  /** V2 model family id (== ScoringModel.family, the cs-api model_id). */
  modelId: string;
  composite?: ScoreRange;
  tiers?: string[];
} & Partial<Record<ModelScorePillar, ScoreRange>>;

export type ModelScoreSlice = {
  entries: ModelScoreEntry[];
};

export const defaultModelScoreSlice: ModelScoreSlice = { entries: [] };

export function hasRange(range: ScoreRange | undefined): boolean {
  return (
    range !== undefined && (range.min !== undefined || range.max !== undefined)
  );
}

export function hasComposite(entry: ModelScoreEntry): boolean {
  return hasRange(entry.composite);
}

export function hasPillars(entry: ModelScoreEntry): boolean {
  return MODEL_SCORE_PILLARS.some((pillar) => hasRange(entry[pillar]));
}

export function hasTiers(entry: ModelScoreEntry): boolean {
  return (entry.tiers?.length ?? 0) > 0;
}

export function hasCriteria(entry: ModelScoreEntry): boolean {
  return hasComposite(entry) || hasTiers(entry) || hasPillars(entry);
}
